import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { Storage } from '@ubud/storage';
import { Config } from '../values/config';
import { User } from '../models/user';
import { forkJoin, Observable, Observer, of } from 'rxjs';
import { Signature } from '../values/signature';
import { fromPromise } from 'rxjs/internal/observable/fromPromise';
import { catchError, map, switchMap, tap } from 'rxjs/internal/operators';
import { plainToClass } from 'class-transformer';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Location } from '@angular/common';
import { ErrorObservable } from 'rxjs-compat/observable/ErrorObservable';

@Injectable()
export class NacoService {
    public readonly VERSION = 'v1';

    private signature: Signature | null = null;
    private user: User | null = null;
    private refreshToken: Signature | null = null;
    private clientSecret: string | null = null;

    public constructor(private config: Config, private router: Router, private storage: Storage, private http: HttpClient, private location: Location) {
    }

    public setUser(user: User | null): void {
        this.user = user;
        this.storage.set('user', user);
    }

    public getUser(): Observable<User | null> {
        if (null !== this.user) {
            return of(this.user);
        }

        const uri = this.getUri('/users/me');

        return fromPromise(this.getSignature()).pipe(
            switchMap((signature: Signature | null) => {
                if (null === signature) {
                    return this.refreshUser();
                }

                const options = {
                    headers: {
                        Authorization: `${signature.type} ${signature.token}`,
                    },
                };

                return this.http.get<any>(uri, options).pipe(
                    map((res: any) => {
                        if (res.data) {
                            return res.data;
                        }

                        throw new Error('There are no body to be transformed');
                    }),
                    map((data: any) => {
                        const authUser: any = plainToClass(User, data);

                        this.setUser(authUser);

                        return authUser;
                    }),
                    catchError((e: HttpErrorResponse) => {
                        if (401 === e.status) {
                            return this.refreshUser().pipe(
                                switchMap((refreshUser: User | null) => {
                                    if (refreshUser) {
                                        return of(refreshUser);
                                    }

                                    return ErrorObservable.create(e);
                                }),
                            );
                        }

                        return ErrorObservable.create(e);
                    }),
                );
            }),
        );
    }

    public getSignature(): Promise<Signature | null> {
        return Promise.resolve(this.signature);
    }

    public setSignature(signature: Signature): void {
        this.signature = signature;
    }

    public setClientSecret(secret: string): void {
        this.clientSecret = secret;
    }

    public setRefreshToken(refreshToken: Signature): void {
        this.refreshToken = refreshToken;
    }

    public refreshUser(scopes?: string): Observable<User | null> {
        this.setUser(null);
        this.setSignature(null);

        return forkJoin(this.getRefreshToken(), this.getClientSecret()).pipe(
            switchMap((results: any) => {
                const refreshToken: Signature = results[0];
                const clientSecret: string = results[1];

                if (refreshToken && clientSecret) {
                    return this.refreshWithRefreshToken(clientSecret, refreshToken.token, scopes);
                }

                return this.refreshWithSilentAuth();
            }),
        );
    }

    public refreshWithRefreshToken(clientSecret: string, refreshToken: string, scopes?: string): Observable<User | null> {
        const request = this.http.post(this.getUri('/tokens'), {
            grant_type: 'refresh_token',
            scopes: scopes || 'basic email',
            client_id: this.config.clientId,
            client_secret: clientSecret,
            refresh_token: refreshToken,
        });

        return request.pipe(
            tap((tokens: any) => {
                if (tokens && tokens.data) {
                    this.setSignature({
                        expiresIn: tokens.data.expires_in,
                        type: tokens.data.token_type,
                        token: tokens.data.access_token,
                    });

                    this.setRefreshToken({
                        type: tokens.data.token_type,
                        token: tokens.data.refresh_token,
                    });
                }
            }),
            map((tokens: any) => tokens && tokens.data),
            switchMap((hasToken: boolean) => {
                if (hasToken) {
                    return this.getUser();
                }

                return of(null);
            }),
        );
    }

    public refreshWithSilentAuth(): Observable<User | null> {
        return new Observable<User | null>((observer: Observer<User | null>) => {
            const redirectUri = this.getCurrentOrigin('auth/silent');
            const uri = this.login('basic email', redirectUri);

            const iframe = document.createElement('iframe');
            iframe.setAttribute('src', uri);
            iframe.style.display = 'none';

            iframe.addEventListener('load', async () => {
                try {
                    const queryString = iframe.contentWindow.location.search.substring(1);

                    iframe.parentNode.removeChild(iframe);

                    if (queryString) {
                        const queryParts = queryString.split('&');
                        const params = {};

                        for (const item of queryParts) {
                            const itemParts = item.split('=');

                            params[itemParts[0]] = itemParts[1];
                        }

                        if (params.hasOwnProperty('access_token')) {
                            const state = params['state'] || null;

                            if (state !== await this.getState()) {
                                observer.next(null);

                                return;
                            }

                            this.setSignature({
                                token: params['access_token'],
                                type: params['token_type'],
                                expiresIn: params['ttl'],
                            });

                            const user = await this.getUser().toPromise();

                            if (user) {
                                this.setUser(user);
                            }

                            observer.next(user);
                        } else {
                            observer.next(null);
                        }

                        return;
                    }

                    observer.next(null);
                } catch (e) {
                    observer.next(null);
                }
            }, false);

            document.body.appendChild(iframe);
        });
    }

    public login(scopes: string, redirectUri?: string): string {
        if (!redirectUri) {
            redirectUri = this.router.url;
        }

        const state = this.generateState();

        this.storage.set('state', state);

        const query: string = this.buildQueryString(
            {
                'response_type': 'token',
                'scopes': scopes,
                'client': this.config.clientId,
                'state': state,
                'continue': redirectUri,
            },
        );

        return this.config.endpoint + '/auth?' + query;
    }

    public logout(redirectUri?: string): Promise<string> {
        this.signature = null;
        this.user = null;

        return new Promise(resolve => {
            Promise.all([
                this.storage.remove('signature'),
                this.storage.remove('user'),
            ]).then(() => {
                let endpoint = this.config.endpoint + '/auth/logout';

                if (redirectUri) {
                    endpoint += '?continue=' + redirectUri;
                }

                resolve(endpoint);
            });
        });
    }

    public getState(): Promise<string | null> {
        return this.storage.get('state');
    }

    public buildNestedUri(route: ActivatedRouteSnapshot): string {
        let uri = '';

        if (route.routeConfig && route.routeConfig.path) {
            uri += '/' + route.routeConfig.path;
        }

        if (route.children) {
            route.children.forEach((item: ActivatedRouteSnapshot) => {
                uri += this.buildNestedUri(item);
            });
        }

        return uri;
    }

    private getRefreshToken(): Promise<Signature | null> {
        if (null !== this.refreshToken) {
            return Promise.resolve(this.refreshToken);
        }

        return this.storage.get('refresh_token');
    }

    private getClientSecret(): Promise<string | null> {
        if (null !== this.clientSecret) {
            return Promise.resolve(this.clientSecret);
        }

        return this.storage.get('client_secret');
    }

    private getUri(path: string): string {
        return this.config.endpoint + '/api/' + this.VERSION + path;
    }

    public getCurrentOrigin(path?: string): string {
        return window.location.origin + this.location.prepareExternalUrl(path || '');
    }

    private generateState(): string {
        const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let text = '';

        for (let i = 0; i < 5; i++) {
            text += possible.charAt(Math.floor(Math.random() * possible.length));
        }

        return text;
    }

    private buildQueryString(params: object): string {
        const queryParams = Object.keys(params).map((key: string) => {
            return encodeURIComponent(key) + '=' + encodeURIComponent(params[key]);
        });

        return queryParams.join('&');
    }
}
